import {useCallback, useMemo} from "react";

import {
    type Action,
    type ActionBubble,
    type ActionPromiseCallbacks,
    type ChatInputMetadata,
} from "@khan/guide-types";

import {shouldSendMessageForActionBubble} from "./should-send-message-for-action-bubble.ts";
import {
    useMakeActionBubbleHandlers,
    type OpenModalActionButtonCallback,
} from "./use-make-action-bubble-handlers.ts";
import {useSendMessageForActionBubble} from "./use-send-message-for-action-bubble.tsx";

interface Args {
    /**
     * Callbacks that are invoked when async actions succeed or fail.
     */
    asyncCallbacks: ActionPromiseCallbacks;
    /**
     * The method used to trigger a modal from an action bubble.
     */
    openModal: OpenModalActionButtonCallback;
    /**
     * The method used to send a chat message from an action bubble.
     */
    sendChatMessage: (message: string, metadata: ChatInputMetadata) => void;
}

/**
 * A hook that returns a method for handling actions invoked by Khanmigo
 * Chat action bubbles.
 */
export const useActionBubbleHandler = ({
    asyncCallbacks,
    openModal,
    sendChatMessage,
}: Args) => {
    const makeActionBubbleHandlers = useMakeActionBubbleHandlers();
    const sendMessageForActionBubble =
        useSendMessageForActionBubble(sendChatMessage);

    const actionHandlers = useMemo(
        () =>
            makeActionBubbleHandlers({
                openModal,
                asyncCallbacks,
            }),
        [asyncCallbacks, makeActionBubbleHandlers, openModal],
    );

    const invokeActions = useCallback(
        (actions: Readonly<Action[]>) => {
            actions.forEach(({actionType, parameters}) => {
                actionHandlers[actionType](parameters);
            });
        },
        [actionHandlers],
    );

    return useCallback(
        (actionBubble: ActionBubble) => {
            const actions = actionBubble.actions ?? [];
            invokeActions(actions);

            if (shouldSendMessageForActionBubble(actionBubble)) {
                sendMessageForActionBubble(actionBubble);
            }
        },
        [invokeActions, sendMessageForActionBubble],
    );
};
